import { useState, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Sparkles } from "lucide-react";
import { cn, useTranslation } from "@/packages/utils";
import { springs } from "@/components/ui/motion";
import type { DepthTier, ProfilingAnswers, ProfilingState } from "./types";

// ─── Props ──────────────────────────────────────────────────────────────────

interface StepProfilingProps {
  profilingState: ProfilingState;
  onStateChange: (state: ProfilingState) => void;
}

// ─── Tier Recommendation Logic ──────────────────────────────────────────────

function computeRecommendedTier(answers: ProfilingAnswers): DepthTier {
  // surveillance/well-established → deep-dive
  if (
    answers.goal === "surveillance-audit" ||
    answers.maturity === "well-established"
  ) {
    return "deep-dive";
  }
  // 201-500 employees → deep-dive
  if (answers.companySize === "201-500") {
    return "deep-dive";
  }
  // small + just-starting + initial-cert → basic
  if (
    (answers.companySize === "1-20" || answers.companySize === "21-50") &&
    answers.maturity === "just-starting" &&
    answers.goal === "initial-certification"
  ) {
    return "basic";
  }
  // default → standard
  return "standard";
}

// ─── Constants ──────────────────────────────────────────────────────────────

const COMPANY_SIZE_OPTIONS = ["1-20", "21-50", "51-200", "201-500"] as const;

const MATURITY_OPTIONS = [
  "just-starting",
  "partially-implemented",
  "well-established",
] as const;

const GOAL_OPTIONS = [
  "initial-certification",
  "surveillance-audit",
  "internal-review",
] as const;

const TIER_ORDER: DepthTier[] = ["basic", "standard", "deep-dive"];

function isCompleteAnswers(
  answers: Partial<ProfilingAnswers>,
): answers is ProfilingAnswers {
  return (
    answers.companySize !== undefined &&
    answers.maturity !== undefined &&
    answers.goal !== undefined
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

export function StepProfiling({
  profilingState,
  onStateChange,
}: StepProfilingProps) {
  const { t } = useTranslation("audits");
  const { answers, recommendedTier, selectedTier } = profilingState;
  const [showTiers, setShowTiers] = useState(recommendedTier !== null);

  const allAnswered = useMemo(
    () =>
      answers.companySize !== undefined &&
      answers.maturity !== undefined &&
      answers.goal !== undefined,
    [answers.companySize, answers.maturity, answers.goal],
  );

  const handleAnswer = useCallback(
    <K extends keyof ProfilingAnswers>(key: K, value: ProfilingAnswers[K]) => {
      const newAnswers = { ...answers, [key]: value };

      if (isCompleteAnswers(newAnswers)) {
        const recommended = computeRecommendedTier(newAnswers);
        onStateChange({
          answers: newAnswers,
          recommendedTier: recommended,
          selectedTier: recommended,
        });
        // Delay showing tiers for a nice animation
        setTimeout(() => setShowTiers(true), 200);
      } else {
        onStateChange({
          answers: newAnswers,
          recommendedTier: null,
          selectedTier: null,
        });
      }
    },
    [answers, onStateChange],
  );

  const handleTierSelect = useCallback(
    (tier: DepthTier) => {
      onStateChange({
        ...profilingState,
        selectedTier: tier,
      });
    },
    [profilingState, onStateChange],
  );

  return (
    <div className="flex flex-col gap-6 py-4">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-sm font-medium text-primary mb-1">
          {t("wizard.profiling.title")}
        </h3>
        <p className="text-[12px] text-muted">
          {t("wizard.profiling.subtitle")}
        </p>
      </div>

      {/* Question 1: Company Size */}
      <QuestionGroup
        label={t("wizard.profiling.companySize.label")}
        options={COMPANY_SIZE_OPTIONS.map((opt) => ({
          value: opt,
          label: t(`wizard.profiling.companySize.options.${opt}`),
        }))}
        selected={answers.companySize}
        onSelect={(val) => handleAnswer("companySize", val)}
      />

      {/* Question 2: Maturity */}
      <QuestionGroup
        label={t("wizard.profiling.maturity.label")}
        options={MATURITY_OPTIONS.map((opt) => ({
          value: opt,
          label: t(`wizard.profiling.maturity.options.${opt}`),
        }))}
        selected={answers.maturity}
        onSelect={(val) => handleAnswer("maturity", val)}
      />

      {/* Question 3: Goal */}
      <QuestionGroup
        label={t("wizard.profiling.goal.label")}
        options={GOAL_OPTIONS.map((opt) => ({
          value: opt,
          label: t(`wizard.profiling.goal.options.${opt}`),
        }))}
        selected={answers.goal}
        onSelect={(val) => handleAnswer("goal", val)}
      />

      {/* Tier Selection (shown after all questions answered) */}
      <AnimatePresence>
        {showTiers && allAnswered && recommendedTier && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={springs.smooth}
            className="flex flex-col gap-4"
          >
            {/* Recommendation explanation */}
            <div className="flex items-start gap-2 rounded-lg bg-accent/5 border border-accent/20 px-3 py-2.5">
              <Sparkles size={14} className="shrink-0 mt-0.5 text-accent" />
              <div className="flex flex-col gap-0.5">
                <span className="text-[12px] font-medium text-accent">
                  {t("wizard.profiling.recommendation.label")}
                </span>
                <span className="text-[11px] text-muted leading-relaxed">
                  {t(
                    `wizard.profiling.recommendation.explanation.${recommendedTier}`,
                  )}
                </span>
              </div>
            </div>

            {/* Tier Cards */}
            <div className="grid gap-2">
              {TIER_ORDER.map((tier) => (
                <TierCard
                  key={tier}
                  tier={tier}
                  isSelected={selectedTier === tier}
                  isRecommended={recommendedTier === tier}
                  onSelect={() => handleTierSelect(tier)}
                />
              ))}
            </div>

            {/* Override hint */}
            <p className="text-[11px] text-muted text-center">
              {t("wizard.profiling.override")}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── QuestionGroup ──────────────────────────────────────────────────────────

interface QuestionGroupProps<T extends string> {
  label: string;
  options: { value: T; label: string }[];
  selected: T | undefined;
  onSelect: (value: T) => void;
}

function QuestionGroup<T extends string>({
  label,
  options,
  selected,
  onSelect,
}: QuestionGroupProps<T>) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-[12px] font-medium text-primary">{label}</span>
      <div className="grid gap-1.5">
        {options.map((opt) => {
          const isSelected = selected === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onSelect(opt.value)}
              className={cn(
                "flex items-center gap-2.5 rounded-lg border px-3 py-2 text-left text-[12px] transition-all duration-150",
                isSelected
                  ? "border-accent bg-accent/5 text-accent font-medium"
                  : "border-border hover:border-accent/40 text-primary",
              )}
            >
              <div
                className={cn(
                  "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
                  isSelected ? "border-accent bg-accent" : "border-border",
                )}
              >
                {isSelected && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={springs.bouncy}
                  >
                    <Check className="h-2.5 w-2.5 text-white" />
                  </motion.div>
                )}
              </div>
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── TierCard ───────────────────────────────────────────────────────────────

interface TierCardProps {
  tier: DepthTier;
  isSelected: boolean;
  isRecommended: boolean;
  onSelect: () => void;
}

function TierCard({
  tier,
  isSelected,
  isRecommended,
  onSelect,
}: TierCardProps) {
  const { t } = useTranslation("audits");

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "relative flex items-start gap-3 rounded-lg border px-3 py-2.5 text-left transition-all duration-150",
        isSelected
          ? "border-accent bg-accent/5 ring-1 ring-accent/30"
          : "border-border hover:border-accent/40",
      )}
    >
      {/* Radio indicator */}
      <div
        className={cn(
          "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
          isSelected ? "border-accent bg-accent" : "border-border",
        )}
      >
        {isSelected && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={springs.bouncy}
          >
            <Check className="h-2.5 w-2.5 text-white" />
          </motion.div>
        )}
      </div>

      {/* Content */}
      <div className="flex flex-col gap-0.5 min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "text-[12px] font-semibold",
              isSelected ? "text-accent" : "text-primary",
            )}
          >
            {t(`wizard.profiling.tiers.${tier}.name`)}
          </span>
          {isRecommended && (
            <span className="rounded-full bg-accent/10 px-2 py-0.5 text-[10px] font-medium text-accent">
              {t("wizard.profiling.recommendation.label")}
            </span>
          )}
        </div>
        <span className="text-[11px] text-muted">
          {t(`wizard.profiling.tiers.${tier}.description`)}
        </span>
        <span className="text-[10px] text-muted/70 mt-0.5">
          {t(`wizard.profiling.tiers.${tier}.itemCount`)}
        </span>
      </div>
    </button>
  );
}
